import { useState } from 'react'
import { VIDEO_REVIEWS, type VideoReview } from '../../data/videoReviews'
import { useGsapContext } from '../../hooks/useGsapReveal'
import { useVideoReviews } from '../../hooks/useVideoReviews'
import { headingReveal, revealOnScroll } from '../../animations/gsapAnimations'
import { useSection } from '../../cms/cms-context'

/* Same construction as the Brands ticker: copies of the list on one track
   sliding by half its width, so the loop never shows a seam even on a wide
   viewport. */

/* A YouTube Short dressed as a native portrait video. Until it is tapped
   the card is only a thumbnail, a scrim and our own play control — no
   YouTube chrome, no iframe cost for two dozen cards. The iframe mounts
   on demand and autoplays, which is allowed because it follows a user
   gesture. */
function VideoCard({
  review,
  hidden,
  playing,
  onPlay,
  onStop,
}: {
  review: VideoReview
  hidden: boolean
  playing: boolean
  onPlay: () => void
  onStop: () => void
}) {
  return (
    <figure className="relative w-[235px] shrink-0 overflow-hidden rounded-2xl border border-line bg-void shadow-card transition duration-300 hover:-translate-y-1 hover:border-lime/40 hover:shadow-lift sm:w-[255px]">
      <div className="relative aspect-[9/16]">
        {playing ? (
          <>
            <iframe
              src={`https://www.youtube-nocookie.com/embed/${review.youtubeId}?autoplay=1&playsinline=1&rel=0&modestbranding=1`}
              title={`Video review — ${review.name}`}
              allow="autoplay; encrypted-media; picture-in-picture"
              allowFullScreen
              className="absolute inset-0 size-full border-0"
            />
            <button
              type="button"
              onClick={onStop}
              aria-label="Close video"
              className="absolute top-2.5 right-2.5 grid size-8 place-items-center rounded-full bg-void/70 text-white backdrop-blur-sm transition hover:bg-void/90"
            >
              <svg
                viewBox="0 0 24 24"
                className="size-4"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              >
                <path d="M6 6l12 12M18 6L6 18" />
              </svg>
            </button>
          </>
        ) : (
          <button
            type="button"
            onClick={onPlay}
            tabIndex={hidden ? -1 : undefined}
            aria-label={`Play video review by ${review.name}`}
            className="group/card absolute inset-0 block w-full cursor-pointer text-left"
          >
            {/* Landscape thumbnails cover-crop cleanly into the portrait
                frame; the subject sits centre in a handover clip anyway. */}
            <img
              src={`https://i.ytimg.com/vi/${review.youtubeId}/hqdefault.jpg`}
              alt=""
              loading="lazy"
              draggable={false}
              className="absolute inset-0 size-full object-cover transition duration-500 group-hover/card:scale-[1.04]"
            />

            {/* Scrim so the white caption reads on any frame. */}
            <span
              aria-hidden="true"
              className="absolute inset-0 bg-gradient-to-t from-void/85 via-void/15 to-void/30"
            />

            {/* Play control — the one piece of chrome the card owns. */}
            <span
              aria-hidden="true"
              className="absolute top-1/2 left-1/2 grid size-14 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full bg-lime shadow-action transition duration-300 group-hover/card:scale-110"
            >
              <svg viewBox="0 0 24 24" className="ml-1 size-5 fill-void">
                <path d="M8 5.5v13l11-6.5z" />
              </svg>
            </span>

            {review.duration ? (
              <span
                aria-hidden="true"
                className="absolute top-3 right-3 rounded-full bg-void/60 px-2 py-1 text-[10.5px] font-medium text-white tabular-nums backdrop-blur-sm"
              >
                {review.duration}
              </span>
            ) : null}

            <figcaption className="absolute inset-x-0 bottom-0 p-4">
              <span className="block truncate text-[14px] font-medium text-white">
                {review.name}
              </span>
              <span className="mt-0.5 block truncate text-[11.5px] text-white/70">
                {review.city}
              </span>
            </figcaption>
          </button>
        )}
      </div>
    </figure>
  )
}

/* The reel drifts left on its own, slow enough to browse; resting the
   pointer on it — or opening a clip — holds it still. One clip plays at a
   time, so two testimonials never talk over each other. */
function VideoReviews() {
  const cms = useSection()

  /* The headline opens like every other section's, and the reel rolls in
     from the right — the direction it is already travelling — before the
     marquee takes it. The slide is on the reel's frame, not the track: the
     track's transform belongs to the CSS loop. */
  const ref = useGsapContext<HTMLElement>((scope) => {
    const reel = scope.querySelector('[data-reel]')
    if (reel) revealOnScroll(reel, { x: 96, y: 0, start: 'top 92%', duration: 1.2 })

    const head = scope.querySelector('[data-head]')
    return head ? headingReveal(head) : undefined
  })
  const [activeKey, setActiveKey] = useState<string | null>(null)
  const query = useVideoReviews()

  /* The reviews the console has filed, when there are any; the placeholder
     reel otherwise. An API that is down or empty is the same answer here —
     the section never renders blank. */
  const reel: VideoReview[] =
    query.data && query.data.length > 0
      ? query.data.map((row) => ({
          id: row.id,
          youtubeId: row.youtubeId,
          name: row.name,
          city: row.city,
        }))
      : VIDEO_REVIEWS

  /* The track is two identical halves — the animation slides it by exactly
     half — and each half needs a dozen cards to clear a wide viewport even
     when the console has filed only a review or two. */
  const half = Math.max(2, Math.ceil(12 / reel.length))
  const copies = Array.from({ length: half * 2 }, (_, index) => index)

  return (
    <section
      ref={ref}
      id="video-reviews"
      aria-labelledby="video-reviews-title"
      className="overflow-hidden py-24 sm:py-32"
    >
      <div className="mx-auto max-w-[1320px] px-5 sm:px-8">
        <div data-head className="max-w-2xl">
          <p
            data-heading="eyebrow"
            {...cms.field('eyebrow')}
            className="flex items-center gap-3 label text-lime"
          >
            <span aria-hidden="true" data-heading="rule" className="h-px w-9 coachline" />
            {cms.text('eyebrow', 'Delivery day')}
          </p>
          <h2
            id="video-reviews-title"
            data-heading="title"
            {...cms.field('title')}
            className="mt-5 font-display text-display-2 text-platinum"
          >
            {cms.text('title', 'Hear it from')}{' '}
            <span className="text-lime-grad">{cms.text('titleAccent', 'the keys up')}</span>
          </h2>
          <p
            data-heading="body"
            {...cms.field('lede')}
            className="mt-4 max-w-xl text-[15px] leading-relaxed text-steel"
          >
            {cms.text(
              'lede',
              'Thirty-second reviews filmed the day our customers took delivery — unscripted, in their own words.',
            )}
          </p>
        </div>
      </div>

      <div data-reel className="group mt-12 overflow-hidden edge-fade">
        <ul
          className="flex w-max animate-marquee-video items-stretch gap-5 pt-1.5 pb-2 group-hover:[animation-play-state:paused]"
          style={{ animationPlayState: activeKey ? 'paused' : undefined }}
        >
          {copies.map((copy) =>
            reel.map((review) => {
              const key = `${copy}-${review.id}`
              return (
                <li
                  key={key}
                  aria-hidden={copy > 0 || undefined}
                  className="flex shrink-0"
                >
                  <VideoCard
                    review={review}
                    hidden={copy > 0}
                    playing={activeKey === key}
                    onPlay={() => setActiveKey(key)}
                    onStop={() => setActiveKey(null)}
                  />
                </li>
              )
            }),
          )}
        </ul>
      </div>
    </section>
  )
}

export default VideoReviews
